import classNames from 'classnames';
import { Button } from 'antd';
import { useCallback, useState } from 'react';
import { ReactComponent as FaceImage } from '@/assets/images/face-recognation.svg';
import { VideoCapture } from './VideoCapture';
import ImageCapture from './ImageCapture';
import Flex from '../Flex';
import { useTranslation } from 'react-i18next';

export interface CaptureMediaProps {
  description: string;
  buttonStartText: string;
  onError: (error: string | DOMException) => void;
  descriptionClassname?: string;
  type?: 'image' | 'video';
  loading?: boolean;
  onInit?: () => void;
  onSubmit?: (image: File) => void;
}

export function MediaCapture({
  onInit,
  loading,
  onError,
  onSubmit,
  description,
  type = 'image',
  buttonStartText,
  descriptionClassname = undefined,
}: CaptureMediaProps) {

  const [t] = useTranslation(['common', 'face']);

  const [state, setState] = useState<'preview' | 'hint' | 'rules'>('hint');

  const onStartClick = useCallback(() => {
    setState('preview');
    if (onInit) {
      onInit();
    }
  }, [onInit]);

  const onShowRulesClick = () => {
    setState('rules');
  };

  return (
    <Flex
      align="center"
      direction="column"
      className="bg-gray-100 p-2 rounded-lg h-full max-h-full max-w-sm mx-auto">
      {state === 'preview' &&
        (type === 'video' ?
          <VideoCapture onError={onError} loading={loading} onConfirm={onSubmit} /> :
          <ImageCapture onError={onError} loading={loading} onConfirm={onSubmit} />
        )
      }
      {state === 'hint' &&
        <Flex className="h-full my-2" direction="column" align="center">
          <Flex
            direction="column"
            justify="center"
            className="w-full h-full">
            <FaceImage height="160px" />
            <p className={classNames('px-3 leading-relaxed text-base text-center', descriptionClassname)}>
              {description}
            </p>
            <Button
              size="large"
              type="primary"
              className="mx-auto"
              onClick={onShowRulesClick}
            >{buttonStartText}</Button>
          </Flex>
        </Flex>
      }
      {state === 'rules' &&
        <Flex className="h-full my-2" direction="column" align="center">
          <Flex
            direction="column"
            justify="center"
            className="w-full h-full">
            <div className={classNames('px-3 leading-relaxed text-base text-center', descriptionClassname)}>
              <h4>
                {t('face:messages.rules-title')}
              </h4>
              <p>{t('face:messages.rules.rule-1')}</p>
              <p>{t('face:messages.rules.rule-2')}</p>
              <p>{t('face:messages.rules.rule-3')}</p>
              <p>{t('face:messages.rules.rule-4')}</p>
              <p>{t('face:messages.rules.rule-5')}</p>
            </div>
            <Button
              size="large"
              type="primary"
              className="mx-auto"
              onClick={onStartClick}
            >{t('face:labels.accept-and-continue')}</Button>
          </Flex>
        </Flex>
      }
    </Flex>
  );
}

export default MediaCapture;
